import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import { useAuth } from "../context/auth-context";
import { markSubscriptionSelected } from "@/lib/api";
import { Loader2, CheckCircle2, Shield, CreditCard } from "lucide-react";
import { useUserSubscriptions } from "@/hooks/use-user-subscriptions";
import { CompanySelectorModal } from "./company-selector-modal";

interface SubscriptionSelectorModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  redirectTo?: string;
}

export const SubscriptionSelectorModal: React.FC<
  SubscriptionSelectorModalProps
> = ({ open, onOpenChange,   redirectTo = "/" }) => {
  const navigate = useNavigate();
  const { setSelectedSubscriptionId, setSelectedCompanyId } = useAuth();
  const { subscriptions, isLoading: loading, isError } = useUserSubscriptions();
  const [selected, setSelected] = useState<string | null>(null);
  const [showCompanySelector, setShowCompanySelector] = useState(false);
  const [selectedSubscriptionIdValue, setSelectedSubscriptionIdValue] =
    useState<string | null>(null);

  const error = isError ? "No fue posible cargar las suscripciones" : null;

  // Si solo hay una suscripción (no SYSTEM), seleccionarla automáticamente
  const realSubscriptions = subscriptions.filter((s) => !s.isSystem);
  const hasSystemOption = subscriptions.some((s) => s.isSystem);

  useEffect(() => {
    if (
      open &&
      realSubscriptions.length === 1 &&
      !hasSystemOption &&
      !selected
    ) {
      setSelected(realSubscriptions[0].id);
    }
  }, [open, realSubscriptions, hasSystemOption, selected]);

  const handleConfirm = async () => {
    if (selected) {
      setSelectedSubscriptionId(selected);
      setSelectedCompanyId(null);

      try {
        localStorage.setItem("selectedSubscriptionId", selected);
        localStorage.removeItem("selectedCompanyId");
        markSubscriptionSelected();
      } catch (error) {
        console.error(
          "[SUBSCRIPTION-SELECTOR] Error guardando en localStorage:",
          error
        );
      }

      onOpenChange(false);

      if (selected === "SYSTEM") {
        navigate("/", { replace: true });
      } else {
        setSelectedSubscriptionIdValue(selected);
        setShowCompanySelector(true);
      }
    }
  };

  const handleCompanyModalClose = (open: boolean) => {
    setShowCompanySelector(open);
    if (!open && selectedSubscriptionIdValue) {
      // Si cerraron el modal de sociedad sin seleccionar, volver a abrir suscripciones
      onOpenChange(true);
    }
  };

  const canConfirm = Boolean(selected);

  return (
    <>
      <Dialog open={open && !showCompanySelector} onOpenChange={onOpenChange}>
        <DialogContent className="w-[95vw] max-w-lg mx-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold flex items-center gap-2">
              <CreditCard className="h-5 w-5 text-primary" />
              Selecciona una suscripción
            </DialogTitle>
            <DialogDescription className="text-sm pt-1">
              Elige la suscripción con la que deseas trabajar en esta sesión.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {loading && (
              <div className="flex flex-col items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-primary mb-3" />
                <div className="text-sm text-muted-foreground">
                  Cargando suscripciones...
                </div>
              </div>
            )}

            {error && (
              <div className="rounded-md bg-destructive/10 p-4 text-center">
                <div className="text-sm font-medium text-destructive">
                  {error}
                </div>
              </div>
            )}

            {!loading && !error && subscriptions.length === 0 && (
              <div className="text-center py-12">
                <CreditCard className="h-12 w-12 text-muted-foreground mx-auto mb-3 opacity-50" />
                <div className="text-sm font-medium text-muted-foreground mb-1">
                  No tienes acceso a ninguna suscripción
                </div>
                <div className="text-xs text-muted-foreground">
                  Contacta al administrador para obtener acceso
                </div>
              </div>
            )}

            {!loading && !error && subscriptions.length > 0 && (
              <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
                {subscriptions
                  .filter((s) => s.active !== false && !s.isSystem)
                  .map((sub) => (
                    <Card
                      key={sub.id}
                      className={`cursor-pointer transition-all duration-200 ${
                        selected === sub.id
                          ? "ring-2 ring-primary ring-offset-2 bg-primary/5 shadow-md"
                          : "hover:bg-muted/50 hover:shadow-sm"
                      }`}
                      onClick={() => setSelected(sub.id)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          setSelected(sub.id);
                        }
                      }}
                      role="button"
                      tabIndex={0}
                      aria-selected={selected === sub.id}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between gap-3">
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            <span
                              className={`h-3 w-3 rounded-full flex-shrink-0 animate-pulse ${
                                sub.status === "active"
                                  ? "bg-green-500"
                                  : "bg-red-500"
                              }`}
                            />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <h3 className="font-semibold text-base truncate">
                                  {sub.description || sub.planName}
                                </h3>
                              </div>
                              <p className="text-xs text-muted-foreground mt-0.5">
                                {sub.planId
                                  ? `(${sub.planId.slice(0, 8)}...)`
                                  : ""}
                              </p>
                            </div>
                          </div>
                          <div className="flex-shrink-0">
                            {selected === sub.id ? (
                              <CheckCircle2 className="h-5 w-5 text-primary" />
                            ) : (
                              <div className="w-5 h-5 rounded-full border-2 border-muted-foreground/30" />
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}

                {hasSystemOption && (
                  <>
                    <div className="relative my-4">
                      <div className="absolute inset-0 flex items-center">
                        <span className="w-full border-t" />
                      </div>
                      <div className="relative flex justify-center text-xs uppercase">
                        <span className="bg-background px-2 text-muted-foreground">
                          Plataforma
                        </span>
                      </div>
                    </div>
                    <Card
                      className={`cursor-pointer transition-all duration-200 ${
                        selected === "SYSTEM"
                          ? "ring-2 ring-amber-500 ring-offset-2 bg-amber-50 shadow-md dark:bg-amber-950/20"
                          : "hover:bg-muted/50 hover:shadow-sm border-amber-200 dark:border-amber-800"
                      }`}
                      onClick={() => setSelected("SYSTEM")}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          setSelected("SYSTEM");
                        }
                      }}
                      role="button"
                      tabIndex={0}
                      aria-selected={selected === "SYSTEM"}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between gap-3">
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            <div className="flex-shrink-0">
                              <div
                                className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                                  selected === "SYSTEM"
                                    ? "bg-amber-500 text-white"
                                    : "bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400"
                                }`}
                              >
                                <Shield className="h-5 w-5" />
                              </div>
                            </div>
                            <div className="flex-1 min-w-0">
                              <h3 className="font-semibold text-base">
                                Plataforma
                              </h3>
                              <p className="text-xs text-muted-foreground mt-0.5">
                                Administrar datos globales del sistema
                              </p>
                            </div>
                          </div>
                          <div className="flex-shrink-0">
                            {selected === "SYSTEM" ? (
                              <CheckCircle2 className="h-5 w-5 text-amber-500" />
                            ) : (
                              <div className="w-5 h-5 rounded-full border-2 border-muted-foreground/30" />
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </>
                )}
              </div>
            )}
          </div>

          {!loading && !error && subscriptions.length > 0 && (
            <div className="flex flex-col sm:flex-row justify-end gap-2 sm:gap-3 pt-2 border-t">
              <Button
                variant="outline"
                onClick={() => onOpenChange(false)}
                className="w-full sm:w-auto"
              >
                Cancelar
              </Button>
              <Button
                onClick={handleConfirm}
                disabled={!canConfirm}
                className="w-full sm:w-auto"
              >
                Continuar
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {selectedSubscriptionIdValue && (
        <CompanySelectorModal
          open={showCompanySelector}
          onOpenChange={handleCompanyModalClose}
          redirectTo={redirectTo}
        />
      )}
    </>
  );
};
